import _ from "lodash";
import { useState } from "react";
import {
  TableRow,
  TableCell,
  Stack,
  Typography,
  Tooltip,
  Paper,
  Grid,
  SvgIcon,
  IconButton,
} from "@mui/material";
import columns from "./colums";
import PaginatedTable from "../../../utils/table/PaginatedTable";
import moment from "moment";
import { useNavigate } from "../../../utils/hooks";
import { EventRepeat as RepeatIcon, Cancel as CanceledIcon, CheckCircle as ActiveIcon } from "@mui/icons-material";
import Repeater from "./Repeater";
import { useAuth } from "../../../auth/context";
import Content from "../../../utils/medicalDocuments/Content";
import Type from "../../../utils/medicalDocuments/Type";

const notRowsMessage = "No hay recetas/órdenes para mostrar";

const Status = ({ medicalDocument }) => {
  const { status } = medicalDocument;

  if (status == "canceled") {
    return <CanceledIcon color="error" />;
  }
  return <ActiveIcon color="success" />;
};

const Actions = ({ user, medicalDocument, onRepeat }) => {
  return user.canRepeat(medicalDocument) &&
    <TableCell component="th" scope="row" align="center">
      <IconButton
        onClick={(event) => {
          event.stopPropagation();
          onRepeat();
        }}
        >
        <RepeatIcon />
      </IconButton>
    </TableCell>
};

//TODO avoid repeating type translation
const RowGetter = ({ row: medicalDocument }) => {
  const [repeaterOpen, setRepeaterOpen] = useState(false);
  const toggleRepeater = () => setRepeaterOpen((repeaterOpen) => !repeaterOpen);
  const navigate = useNavigate();
  const { id, patient, type, diagnosis, date } = medicalDocument;
  const { user } = useAuth();

  return (
    <>
      <TableRow
        hover
        role="checkbox"
        tabIndex={-1}
        key={id}
        onClick={() => navigate(`/details-document/${id}`)}
      >
        <TableCell component="th" scope="row">
          {id}
        </TableCell>

        <TableCell component="th" scope="row" sx={{ minWidth: 155 }}>
          {patient.name} {patient.surname}
        </TableCell>

        <TableCell component="th" scope="row">
          {patient.documentNumber}
        </TableCell>

        <TableCell component="th" scope="row">
          <Type title={type} />
        </TableCell>

        <Tooltip
          arrow
          title={<Content medicalDocument={medicalDocument} isTooltip={true} />}
          placement="top"
          enterTouchDelay={0}
        >
          <TableCell component="th" scope="row" sx={{ minWidth: 200 }}>
            <Content medicalDocument={medicalDocument} truncated />
          </TableCell>
        </Tooltip>

        <TableCell component="th" scope="row" sx={{ maxWidth: 300 }}>
          {diagnosis}
        </TableCell>

        <TableCell component="th" scope="row">
          {moment(date).format("DD/MM/YYYY")}
        </TableCell>

        <TableCell component="th" scope="row" align="center">
          <Status medicalDocument={medicalDocument} />
        </TableCell>
        
        <Actions user={user} medicalDocument={medicalDocument} onRepeat={toggleRepeater}/>
      </TableRow>
      {repeaterOpen && (
        <Repeater
          medicalDocument={medicalDocument}
          onClose={toggleRepeater}
          repeaterOpen={repeaterOpen}
        />
      )}
    </>
  );
};

const CardGetter = ({ row: medicalDocument }) => {
  const navigate = useNavigate();
  const [repeaterOpen, setRepeaterOpen] = useState(false);
  const toggleRepeater = () => setRepeaterOpen((repeaterOpen) => !repeaterOpen);
  const { id, patient, type, date } = medicalDocument;
  const { user } = useAuth();

  return (
    <>
      <Paper
        variant="outlined"
        hover
        key={id}
        onClick={() =>
          navigate(`/details-document/${id}`, {
            state: { idDocument: id },
          })
        }
        sx={{ p: 1, mt: 1, mb: 1 }}
      >
        <Grid container alignItems="center">
          <Grid item xs={9}>
            <Typography sx={{ fontWeight: 600 }}>
              {patient.name} {patient.surname}
            </Typography>
            <Typography>DNI: {patient.documentNumber}</Typography>
          </Grid>
          {
          user.canRepeat(medicalDocument) &&
            <Grid item xs={3} alignSelf="start">
              <Stack direction="row" spacing={.5} alignItems="center">
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleRepeater();
                  }}
                >
                  <SvgIcon sx={{ color: "primary.main", fontSize: 25 }}>
                    <RepeatIcon />
                  </SvgIcon>
                </IconButton>
                <Status medicalDocument={medicalDocument} />
              </Stack>
            </Grid>
          }
        </Grid>

        <Grid container alignItems="center" mt={1.5}>
          <Grid item xs={12}>
            <Content medicalDocument={medicalDocument} truncated />
          </Grid>
        </Grid>
        <Grid container alignItems="center" mt={1.5}>
          <Grid item xs={9}>
            <Typography>EMISIÓN {moment(date).format("DD/MM/YYYY")}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Type title={type} />
          </Grid>
        </Grid>
      </Paper>
      {repeaterOpen && (
        <Repeater
          medicalDocument={medicalDocument}
          onClose={toggleRepeater}
          repeaterOpen={repeaterOpen}
        />
      )}
    </>
  );
};

const PrescriptionsTable = ({
  medicalDocuments,
  loading,
  page,
  rowsPerPage,
  setPage,
  count,
  handleRequestSort,
}) => {
  function toRowOrderField(medicalDocument, sortingField) {
    const sortingFieldId = sortingField.id;
    const { id } = medicalDocument;
    switch (sortingFieldId) {
      case "id": {
        return parseInt(id, 10);
      }
      default: {
        return _.get(medicalDocument, sortingFieldId);
      }
    }
  }

  function handleChangePage(event, value) {
    setPage(value);
  }

  const pagination = {
    page,
    rowsPerPage,
    handleChangePage,
    count,
  };

  return (
    <PaginatedTable
      rows={medicalDocuments}
      columns={columns}
      RowGetter={RowGetter}
      notRowsMessage={notRowsMessage}
      toRowOrderField={toRowOrderField}
      loading={loading}
      pagination={pagination}
      CardGetter={CardGetter}
      handleRequestSort={handleRequestSort}
      defaultSortingFieldId={undefined}
    />
  );
};

export default PrescriptionsTable;
